// Display-name validation (Foundations F2; hardened F4). A mirror of the
// authoritative Postgres gate (enforce_display_name trigger): the server/DB is
// the real authority — this exists for instant client-side UX feedback before a
// network round-trip. Keep the two in sync; divergence is safe (the DB wins).

export type DisplayNameError = "too_short" | "too_long" | "charset" | "format" | "profanity";

export const DISPLAY_NAME_MIN = 2;
export const DISPLAY_NAME_MAX = 24;

const CHARSET = /^[A-Za-z0-9 _.\-]+$/;
// Starter blocklist — mirrors the SQL trigger; SEC/LEGAL extend it later.
const BLOCKLIST = ["admin", "administrator", "moderator", "fuck", "shit", "cunt", "bitch", "rape"];
const SUBSTRING_BLOCKLIST = ["nigger", "faggot"];
// Invisible code points stripped before validating — used for impersonation /
// confusables (zero-width space/non-joiner/joiner U+200B-200D, BOM U+FEFF, soft
// hyphen U+00AD). The DB trigger strips the same set. Listed numerically so the
// source stays pure-ASCII (no invisible chars in the file).
const INVISIBLE_CODES = new Set([0x200b, 0x200c, 0x200d, 0xfeff, 0x00ad]);

function stripInvisible(value: string): string {
  let out = "";
  for (const ch of value) {
    if (!INVISIBLE_CODES.has(ch.codePointAt(0) ?? 0)) out += ch;
  }
  return out;
}

/**
 * Canonical display-name form (F4 hardening): strip invisible chars, collapse
 * internal whitespace runs to one space, trim. The DB trigger applies the SAME
 * normalization (it's the authority); the client normalizes too so the optimistic
 * UI matches exactly what the server stores.
 */
export function normalizeDisplayName(raw: string): string {
  return stripInvisible(raw).replace(/\s+/g, " ").trim();
}

/**
 * Validate a (raw) display name against the same rules the DB enforces, after
 * normalization. Returns the first failing rule, or `null` when allowed. An
 * empty/whitespace-only name means "clear it" (allowed → guest label).
 */
export function validateDisplayName(raw: string): DisplayNameError | null {
  const name = normalizeDisplayName(raw);
  if (name === "") return null;
  if (name.length < DISPLAY_NAME_MIN) return "too_short";
  if (name.length > DISPLAY_NAME_MAX) return "too_long";
  if (!CHARSET.test(name)) return "charset";
  // Must start AND end alphanumeric — blocks leading/trailing punctuation and
  // all-punctuation impersonation names ("...", "_admin_", "-x-").
  if (!/^[A-Za-z0-9]/.test(name) || !/[A-Za-z0-9]$/.test(name)) return "format";
  const lower = name.toLowerCase();
  if (BLOCKLIST.includes(lower) || SUBSTRING_BLOCKLIST.some((s) => lower.includes(s))) return "profanity";
  return null;
}
